// "Export to Excel" on Show Report: the customer's weekly file (Documentation/excel-report) for the selected trials -
// "All data", the chosen layout's sheet, and Colors. All data comes from POST /v1/trials/export-rows, 250 trials at a
// time; the layout sheet from POST /v1/trials/{ids} (as the report on screen), LAYOUT_PAGE_SIZE trials at a time. If
// any page fails, the export stops and nothing is downloaded (never half a file).
import api from '@/api';
import { parseLayoutFields } from '@/components/Reports/layouts';
import { downloadFile, workbookBlob } from '../excelExport';
import { exportLimit } from './exportLimits';
import { buildExcelReport } from './workbook';

export { MAX_EXPORT_TRIALS, MAX_EXPORT_TRIALS_WITH_TASKS, exportLimit, hasTaskFields } from './exportLimits';
export const EXPORT_PAGE_SIZE = 250; // export-rows' maximum (each row has all the trial's fields and tasks)
export const LAYOUT_PAGE_SIZE = 200; // trials with all their relations and diaries per call

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const fileNamePart = (text) => String(text ?? '').replace(/[\\/:*?"<>|]+/g, ' ').trim().replace(/\s+/g, '_');

/**
 * "Agromate_<DDMMMYYYY>_<initials>.xlsx" as the customer names the file, e.g. Agromate_23OCT2026_RM.xlsx: the local
 * date, and the user's initials (their name if they have none; left out without a user).
 */
export function excelReportFileName(date, user) {
  const day = `${String(date.getDate()).padStart(2, '0')}${MONTHS[date.getMonth()]}${date.getFullYear()}`;
  const who = fileNamePart(user?.initials || user?.name);
  return `${['Agromate', day, who].filter(Boolean).join('_')}.xlsx`;
}

/** The export rows of the trials, in their order, a page at a time; onProgress(done, total) after each page. */
export async function fetchExportRows(ids, onProgress) {
  const rows = [];
  for (let i = 0; i < ids.length; i += EXPORT_PAGE_SIZE) {
    const { data } = await api.post('/trials/export-rows', { ids: ids.slice(i, i + EXPORT_PAGE_SIZE) });
    rows.push(...(data?.trials ?? []));
    onProgress?.(Math.min(i + EXPORT_PAGE_SIZE, ids.length), ids.length);
  }
  return rows;
}

/** The trials as the report on screen gets them (POST /v1/trials/{ids}), a page at a time; onProgress as above. */
export async function fetchLayoutTrials(ids, onProgress) {
  const trials = [];
  for (let i = 0; i < ids.length; i += LAYOUT_PAGE_SIZE) {
    const { data } = await api.post(`/trials/${ids.slice(i, i + LAYOUT_PAGE_SIZE).join(',')}`, {});
    // One id hits POST /trials/{id}, which returns a single object
    trials.push(...(Array.isArray(data) ? data : data ? [data] : []));
    onProgress?.(Math.min(i + LAYOUT_PAGE_SIZE, ids.length), ids.length);
  }
  return trials;
}

/**
 * Fetches the trials, builds the file for the layout ({ name, definition }) and downloads it.
 * onProgress({ phase: 'fetching' | 'fetching-layout' | 'building', done, total }). Throws (nothing downloaded) when a
 * page cannot be fetched, and when there are more trials than the layout's limit (exportLimit). Returns
 * { filename, summary }.
 */
export async function exportExcelReport(ids, user, layout, onProgress) {
  if (ids.length > exportLimit(layout)) throw new Error(`At most ${exportLimit(layout)} trials for this layout`);
  const trials = await fetchExportRows(ids, (done, total) => onProgress?.({ phase: 'fetching', done, total }));
  const layoutTrials = await fetchLayoutTrials(ids, (done, total) => onProgress?.({ phase: 'fetching-layout', done, total }));
  onProgress?.({ phase: 'building', done: ids.length, total: ids.length });
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = user?.name || 'Agromate';
  const exportDate = new Date();
  const summary = buildExcelReport(workbook, trials, exportDate, {
    layout: { name: layout?.name, fields: parseLayoutFields(layout), trials: layoutTrials },
  });
  const filename = excelReportFileName(exportDate, user);
  downloadFile(await workbookBlob(workbook), filename);
  return { filename, summary };
}
